<template>
  <form ref="$form" novalidate @submit.prevent="onSubmit">
    <input type="hidden" name="form_id" :value="content.formId" />
    <input type="hidden" name="form_type" :value="content.type" />
    <input type="hidden" name="referrer" :value="navigation.referrer" />
    <VInput
      v-model="form.firstName"
      :label="t('form-firstname')"
      name="firstname"
      class="form-field"
    />
    <VInput
      v-model="form.lastName"
      :label="t('form-lastname')"
      name="lastname"
      class="form-field"
    />
    <VInput
      v-model="form.company"
      :required="false"
      :label="t('form-project-company')"
      name="company"
      class="form-field"
    />
    <VInput
      v-model="form.email"
      :label="t('form-email')"
      name="email"
      type="email"
      class="form-field"
    />
    <VInput
      v-model="form.phone"
      name="phone"
      :label="t('form-phone')"
      class="form-field"
    />

    <div class="form-field row radio-buttons">
      <div
        class="budget fields-block form-field--inline col-xxs-12 col-xs-6"
        data-input
      >
        <span class="form-title">{{ t('form-project-budget') }}</span>
        <VRadio
          v-for="budget in data.budgetOptions"
          :id="budget.value"
          :key="budget.value"
          v-model="form.budget"
          :required="true"
          name="budget"
          :value="budget.value"
          :label="budget.label"
          class="form-field--small"
        />
        <p class="input__error">{{ t('form-invalid') }}</p>
      </div>

      <div
        class="fields-block form-field--inline col-xxs-12 col-xs-6"
        data-input
      >
        <span class="form-title">{{ t('form-project-deadline') }}</span>
        <VRadio
          v-for="deadline in data.deadlineOptions"
          :id="deadline.value"
          :key="deadline.value"
          v-model="form.deadline"
          :required="true"
          name="deadline"
          :value="deadline.value"
          :label="deadline.label"
          class="form-field--small"
        />
        <p class="input__error">{{ t('form-invalid') }}</p>
      </div>
    </div>

    <VTextarea
      id="textarea"
      v-model="form.message"
      name="description"
      :label="t('form-project-description')"
      class="form-field"
    />

    <Upload
      ref="uploader"
      class="form-field"
      :upload-url="content.uploadUrl"
      @file-added="onFileAdded"
    />

    <!-- eslint-disable  -->
    <VCheckbox
      v-model="form.newsLetter"
      id="newsletter"
      name="newsletter"
      :required="false"
      :label="t('form-newsletter')"
      class="form-field"
    />
    <!-- eslint-enable  -->
    <Feedback v-if="hasError" :content="feedback" class="form-field" />
    <div class="turnstile-widget"></div>

    <div class="row end-xxs">
      <GBtn :disabled="isLoading" type="submit">{{ send }}</GBtn>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { computed, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import Feedback from '@/components/ui/Feedback.vue'
import Upload from '@/components/ui/Upload.vue'
import VCheckbox from '@/components/form/Checkbox.vue'
import VInput from '@/components/form/Input.vue'
import VRadio from '@/components/form/Radio.vue'
import VTextarea from '@/components/form/Textarea.vue'
import {
  getTurnstileToken,
  removeTurnstile,
  stringifyFormDataNewsletter,
  validateForm,
} from '@/modules/form'
import { useNavigationStore } from '@/stores/navigation'
import { useChromeStore } from '@/stores/chrome'
import { logger } from '@/utils/logger'
import { pushForm, formStart } from '@/utils/tracking'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const axios = useAxios()
const navigation = useNavigationStore()
const { t, ...chrome } = useChromeStore()

const feedback = reactive({
  type: 'warning',
  text: t('form-error'),
})

const data = reactive({
  budgetOptions: [
    {
      label: '< 30.000 €',
      value: '< 30.000 €',
    },
    {
      label: '< 50.000 €',
      value: '< 50.000 €',
    },
    {
      label: '< 80.000 €',
      value: '< 80.000 €',
    },
    {
      label: '< 150.000 €',
      value: '< 150.000 €',
    },
    {
      label: '> 150.000 €',
      value: '> 150.000 €',
    },
    {
      label: t('form-project-unknown'),
      value: 'unknown budget',
    },
  ],

  deadlineOptions: [
    {
      label: `< 3 ${t('form-project-months')}`,
      value: '< 3 months',
    },
    {
      label: `< 6 ${t('form-project-months')}`,
      value: '< 6 months',
    },
    {
      label: `> 6 ${t('form-project-months')}`,
      value: '> 6 months',
    },
    {
      label: t('form-project-unknown'),
      value: 'unknown deadline',
    },
  ],
})

const form = reactive({
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  message: '',
  phone: '',
  budget: '',
  deadline: '',
  newsLetter: false,
})

const router = useRouter()
const uploader = ref<InstanceType<typeof Upload>>()
const $form = ref<HTMLFormElement>({} as HTMLFormElement)
const isLoading = ref(false)
const hasError = ref(false)
const fileList = ref<string[]>([])

const send = computed(() =>
  isLoading.value ? t('form-sending') : t('form-send')
)

const onFileAdded = (fileId: string) => {
  fileList.value.push(fileId)
}

onMounted(() => {
  formStart($form.value)
})

const onSubmit = async () => {
  pushForm('click')

  const isValid = validateForm($form.value)

  if (!isValid) {
    return
  }

  isLoading.value = true
  hasError.value = false
  const formData = new FormData($form.value)
  formData.append('tags', 'website,contact-form,prospects')
  let turnstileWidgetID: string

  try {
    const { id, token } = await getTurnstileToken()

    if (token) {
      turnstileWidgetID = id
      formData.append('cf-turnstile-response', token)
    }
  } catch (error) {
    console.error(error)
  }

  if (fileList.value.length) {
    fileList.value.forEach(file => {
      formData.append('uploads[]', file)
    })
  }

  stringifyFormDataNewsletter(formData)

  axios
    .post(
      `${import.meta.env.VITE_API_DOMAIN + props.content.submitUrl}`,
      formData
    )
    .catch(res => {
      hasError.value = true
      pushForm('error')
      logger.error(res)
      isLoading.value = false

      return res
    })
    .then(res => {
      if (res.data?.status === 200) {
        uploader.value?.filePond?.destroy()
        pushForm('success')
        pushForm('success', formData)

        router.push({
          name: `thank-you-${chrome.language}`,
          params: {
            segment: chrome.language === 'fr' ? 'merci' : 'thank-you',
          },
          // https://github.com/vuejs/router/blob/main/packages/router/CHANGELOG.md
          // params: {
          //   content: { type: props.content.type },
          // },
          query: {
            type: props.content.type,
          },
        })
      } else {
        hasError.value = true
        pushForm('error')
        logger.error(res)
        isLoading.value = false
      }
    })
    .finally(() => {
      removeTurnstile(turnstileWidgetID)
    })
}
</script>

<style lang="scss" scoped>
.budget {
  @include mq(xs) {
    margin-bottom: 0;
  }
}

.fields-block {
  &.has-error {
    .form-title {
      border-width: 0 0 3px;
    }
  }
}

.radio-buttons {
  :deep(sup) {
    display: none;
  }
}
</style>
